/**
 * Refuse the one combination that cannot describe a real deal: the landlord collects the tenants'
 * expense reimbursements as income, and carries none of the expenses those reimbursements repay.
 *
 * 🚨🚨 THIS IS WHAT WENT OUT ON 13368 MAIN STREET. The OM stated CAM $54,022, property taxes
 * $46,523, insurance $5,068 and a 3%-of-base-rent management fee - $113,770 in all - against
 * $111,114 of reimbursements. The extractor read every line correctly and they reached the
 * property record. Then an archetype preset zeroed the expense fields while the income figure,
 * which already contained the reimbursements, came in by another path and survived.
 *
 * The model that resulted collected $111,114 of recoveries against $0 of cost:
 *     NOI      $349,318  against the OM's $269,254   (+$80,064, +30%)
 *     Cap       7.46%    against a stated 5.75%
 *     Verdict   GO       on a deal that reads CAUTION
 * on a document carrying his DRE number.
 *
 * ⭐⭐⭐ THE PRESET BUG IS FIXED SEPARATELY; THIS EXISTS BECAUSE THAT IS NOT ENOUGH. The same
 * end-state is reachable by hand-clearing an expense, by a future preset, or by an extraction that
 * finds income but no expense schedule. **A guard on the STATE is worth more than a fix to one of
 * the paths into it** - this repo has now shipped the "preset overwrites document evidence" defect
 * three times (Bakersfield, Stagecoach, and here), each time on a different field.
 *
 * ⛔ IT DOES NOT FIRE ON A TRUE ABSOLUTE-NET DEAL. If the income carries no recoveries and the
 * document itemises nothing, a landlord with zero expenses is exactly right - a ground lease or an
 * absolute-net STNL. The trigger is a CONTRADICTION, never zero expenses on their own.
 *
 * ⭐⭐⭐ THE SECOND TRIGGER EXISTS BECAUSE THE FIRST ONE HAD A HOLE, and a real deal was sitting in
 * it. **1666/1668/1700 E. Highland Avenue**: its OM itemises Real Estate Taxes $11,705, Insurance
 * $5,909, a Gardener $2,400, Parking Lot Sweeping $2,160, Property Maintenance $3,000 and a Capital
 * Improvement Allowance $6,000 - **$31,174** - and the model carried $0, stamped by the same
 * archetype preset. Its income does NOT include recoveries, so the recoveries test stayed silent.
 * A landlord paying a gardener is not absolute-net.
 *
 * ⛔⛔ AND THE DISCRIMINATOR IS THE DOCUMENT'S OWN TOTAL, NOT THE PRESENCE OF LINES. 1345 N Grand
 * Avenue itemises four expense lines too - every one of them **$0**, described "paid by tenant
 * (absolute NNN)". Zero carried against zero itemised is correct and must stay silent. Counting
 * LINES rather than DOLLARS would have flagged a deal that is right.
 */

export interface LandlordExpenseFields {
  property_taxes?: number | string | null;
  insurance?: number | string | null;
  utilities?: number | string | null;
  repairs_maintenance?: number | string | null;
  reserves_capex?: number | string | null;
  other_expenses?: number | string | null;
}

export interface ExpenseConsistencyInput {
  /** The document's own flag: its income line already includes recoveries. */
  incomeIncludesReimbursements: boolean;
  /** Recoveries the document states, whether or not they are inside the income line. */
  expenseReimbursements?: number | string | null;
  /** The document's stated operating-expense total, when it prints one. */
  statedOperatingExpenses?: number | string | null;
  /**
   * The sum of the expense LINES the document itemised. Distinct from `statedOperatingExpenses`:
   * a document can itemise without printing a total, and can itemise lines that are all zero.
   */
  itemisedExpenseTotal?: number | string | null;
  /** What the model currently carries. */
  landlordExpenses: LandlordExpenseFields;
}

export interface ConsistencyWarning {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

/** Form fields arrive as strings; a blank is not a zero, but both are "no expense carried". */
const num = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * The warning, or null when the two halves agree.
 *
 * ⭐ The management fee is deliberately NOT counted as a landlord expense here. It is charged as a
 * percentage and is present in every preset, so counting it would let a model with a management
 * fee and nothing else pass - which is the exact shape that shipped.
 */
export const expenseConsistencyWarning = (
  input: ExpenseConsistencyInput,
): ConsistencyWarning | null => {
  const { incomeIncludesReimbursements, landlordExpenses } = input;
  const reimbursements = num(input.expenseReimbursements);
  const stated = num(input.statedOperatingExpenses);
  const itemised = num(input.itemisedExpenseTotal);

  const carried =
    num(landlordExpenses.property_taxes) +
    num(landlordExpenses.insurance) +
    num(landlordExpenses.utilities) +
    num(landlordExpenses.repairs_maintenance) +
    num(landlordExpenses.reserves_capex) +
    num(landlordExpenses.other_expenses);

  // The model carries a cost. Whatever else is true, the two halves are not contradicting.
  if (carried > 0) return null;

  // ⛔ DOLLARS, NOT LINES. 1345 N Grand itemises four lines, all $0 "paid by tenant".
  if (itemised > 0) {
    return {
      field: 'operating_expenses',
      severity: 'error',
      message:
        `The document itemises ${money(itemised)} of landlord operating expenses and this model ` +
        `carries $0 - taxes, insurance and maintenance are all zero. That overstates NOI by the ` +
        `same ${money(itemised)} and lifts the going-in cap with it. Enter them from the ` +
        `operating statement, or record why the landlord carries none.`,
    };
  }

  const incomeCarriesRecoveries = incomeIncludesReimbursements || reimbursements > 0;
  if (!incomeCarriesRecoveries) return null;

  const evidence = stated > 0
    ? `The document states ${money(stated)} of operating expenses`
    : `The document states ${money(reimbursements)} of reimbursements`;

  return {
    field: 'operating_expenses',
    severity: 'error',
    message:
      `This model collects the tenants' expense reimbursements as income but carries $0 of ` +
      `landlord operating expenses - taxes, insurance and CAM are all zero. ${evidence}, which is ` +
      `the cost those reimbursements repay. Counting one side without the other overstates NOI ` +
      `and the going-in cap. Enter the expenses from the operating statement, or set the income ` +
      `to base rent only.`,
  };
};
